import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import LoginForm from '../../features/auth/components/LoginForm'

interface ProtectedRouteProps {
  children: React.ReactNode
  allowedRoles?: Array<'iso' | 'broker' | 'admin' | 'master_admin'>
  fallback?: React.ReactNode
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  allowedRoles,
  fallback 
}) => {
  const { user, isAuthenticated, isLoading, logout } = useAuth()
  const location = useLocation()

  // Debug logging for protected routes
  console.log('🔍 ProtectedRoute Check:', {
    path: location.pathname,
    userRole: user?.role,
    allowedRoles,
    isAuthenticated,
    user: user ? {email: user.email, role: user.role, status: user.status} : null,
    timestamp: new Date().toISOString()
  })

  // Show loading spinner while checking auth
  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    )
  }

  // If not authenticated or no user object, redirect to login
  if (!isAuthenticated || !user) {
    console.log('🚫 ProtectedRoute: Not authenticated or no user, redirecting to login')
    if (fallback) {
      return <>{fallback}</>
    }
    // Always redirect to login without preserving the "from" location
    // This prevents role confusion on re-login
    return <Navigate to="/login" replace />
  }

  // If role-based access is required, check user role
  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    return (
      <div className="container mt-5">
        <div className="row justify-content-center">
          <div className="col-md-6">
            <div className="alert alert-danger text-center" role="alert">
              <h4 className="alert-heading">Access Denied</h4>
              <p>You don't have permission to access this page.</p>
              <hr />
              <p className="mb-0">
                Your role: <strong>{user.role}</strong><br />
                Required roles: <strong>{allowedRoles.join(', ')}</strong>
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Check if user account is active
  if (user && user.status !== 'active') {
    let statusMessage = ''
    let statusClass = 'alert-warning'
    
    switch (user.status) {
      case 'pending':
        statusMessage = 'Your account is pending approval. Please wait for an administrator to activate your account.'
        break
      case 'suspended':
        statusMessage = 'Your account has been suspended. Please contact an administrator.'
        statusClass = 'alert-danger'
        break
      case 'rejected':
        statusMessage = 'Your account application has been rejected. Please contact an administrator.'
        statusClass = 'alert-danger'
        break
      default:
        statusMessage = 'Your account is not active. Please contact an administrator.'
        statusClass = 'alert-warning'
    }

    return (
      <div className="container mt-5">
        <div className="row justify-content-center">
          <div className="col-md-8">
            <div className={`alert ${statusClass} text-center`} role="alert">
              <h4 className="alert-heading">Account Status: {user.status}</h4>
              <p>{statusMessage}</p>
              <hr />
              <p className="mb-0">
                Account: <strong>{user.email}</strong><br />
                Status: <strong className="text-capitalize">{user.status}</strong>
              </p>
              <div className="mt-3">
                <button 
                  className="btn btn-outline-secondary"
                  onClick={logout}
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // User is authenticated and authorized
  return <>{children}</>
}

export default ProtectedRoute